// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useMemo, useState } from 'react';
import { LineWidgetType, WidgetOutput, ChartOutput } from 'thehive-sdk';
import { WidgetError } from '@Organisms/Dashboard/DashboardDetails/WidgetError';
import { Mix, MixConfig } from '@ant-design/charts';
import {
    convertTypeToAntV,
    dateFormat,
    parseDurationField,
    parseSerieForLabel,
    parseSeriesColor,
    WidgetSerieType,
} from './utils';
import { LanguageSwitcherContext } from '@Lang/LanguageSwitcherContext';
import { ActionWidget } from './ActionWidget';
import { downloadFile } from '@Helpers/download';
import { useFormatDate } from '@Hooks/useFormatDate';
import { slugify } from '@Helpers/string';
import { WidgetLoading } from '@Organisms/Dashboard/DashboardDetails/WidgetLoading/WidgetLoading';
import { UseQueryResult } from 'react-query';
import { useLocalMessage, UseLocalMessageType } from '@Helpers/locale';

import './Chart.scss';

interface LineChartOutput {
    name: string;
    key: string;
    value?: number;
    count?: number;
    time: string;
    color?: string;
    type?: string;
    field?: string;
    label?: string;
}

const prepareChartData = (
    lineData: ChartOutput[] = [],
    options: LineWidgetType,
    i: UseLocalMessageType,
): Record<string, LineChartOutput[]> | undefined =>
    lineData?.reduce(
        (acc, data) => {
            const series = options?.series || [];

            const aggKeys = Object.keys(data).filter((key) => key !== '_key');

            aggKeys.forEach((aggKey, index) => {
                const serieIndex = parseInt(aggKey.replace('agg_', ''));

                if (serieIndex > series.length - 1) {
                    return;
                }

                const serie = series?.[serieIndex];

                const field = {
                    name: parseSerieForLabel(aggKey, serie),
                    key: aggKey,
                    time: new Date(data._key).toISOString(),
                    value: serie.type !== 'bar' ? data[aggKey] || 0 : undefined,
                    count: serie.type === 'bar' ? data[aggKey] || 0 : undefined,
                    color: serie.color,
                    type: serie.type,
                    field: serie.field,
                    label: parseDurationField(serie.field || '', data[aggKey] || 0, i),
                };

                if (serie.type === 'bar') {
                    acc = {
                        ...acc,
                        bar: [...(acc['bar'] || []), field],
                    };
                } else if (serie.type === 'area') {
                    acc = {
                        ...acc,
                        area: [...(acc['area'] || []), field],
                    };
                } else if (serie.type === 'area-spline') {
                    acc = {
                        ...acc,
                        'area-spline': [...(acc['area-spline'] || []), field],
                    };
                } else {
                    acc = {
                        ...acc,
                        [`${aggKey + index}`]: [...(acc[`${aggKey + index}`] || []), field],
                    };
                }
            });

            return acc;
        },

        {} as Record<string, LineChartOutput[]>,
    );

interface LineWidgetProps {
    widget: WidgetOutput;
    query?: UseQueryResult<ChartOutput[]>;
}

export const LineWidget = React.memo(({ widget, query }: LineWidgetProps): ReactElement => {
    const i = useLocalMessage();
    const f = useFormatDate();

    const [chart, setChart] = useState<any>();

    const { language } = useContext(LanguageSwitcherContext);
    const { interval = '1d', series = [], stacked, log } = widget.options || {};

    const dataTreated = useMemo(
        () => prepareChartData(query?.data, widget.options as LineWidgetType, i) || {},
        [query?.data, widget.options, i],
    );

    const config: MixConfig = useMemo(
        () => ({
            plots: Object.entries(dataTreated).map(([, table], index) => {
                const type: WidgetSerieType = (table.find(Boolean)?.type as any) || 'line';

                const typeParsed = convertTypeToAntV(type);

                const lineMaxY = stacked
                    ? Math.max(
                          ...Object.entries(dataTreated).reduce(
                              (_, [, table]) =>
                                  Object.values(
                                      table.reduce(
                                          (acc, { value = 0, time }) => ({
                                              ...acc,
                                              [time]: (acc[time] || 0) + value,
                                          }),
                                          {} as Record<string, number>,
                                      ),
                                  ),
                              [] as number[],
                          ),
                      )
                    : Math.max(
                          ...Object.entries(dataTreated).reduce(
                              (acc, [, table]) => [...acc, Math.max(...table.map(({ value = 0 }) => value))],
                              [] as number[],
                          ),
                      );

                const barMaxY = stacked
                    ? Math.max(
                          ...Object.entries(dataTreated).reduce(
                              (_, [, table]) =>
                                  Object.values(
                                      table.reduce(
                                          (acc, { count = 0, time }) => ({
                                              ...acc,
                                              [time]: (acc[time] || 0) + count,
                                          }),
                                          {} as Record<string, number>,
                                      ),
                                  ),
                              [] as number[],
                          ),
                      )
                    : Math.max(
                          ...Object.entries(dataTreated).reduce(
                              (acc, [, table]) => [...acc, Math.max(...table.map(({ count = 0 }) => count))],
                              [] as number[],
                          ),
                      );

                return {
                    type: typeParsed as any,
                    options: {
                        data: table,

                        autoFit: true,
                        padding: 'auto',

                        isGroup: !stacked,
                        isStack: stacked,

                        xField: 'time',
                        yField: typeParsed === 'column' ? 'count' : 'value',
                        seriesField: 'key',

                        color: parseSeriesColor(table, index),

                        smooth: type === 'area-spline' || type === 'spline',

                        columnStyle: {
                            fillOpacity: 0.65,
                        },

                        label:
                            typeParsed === 'column'
                                ? {
                                      position: 'middle',
                                      formatter: ({ count = 0 }) => count || '',
                                  }
                                : undefined,

                        meta: {
                            key: {
                                formatter: (key) => table.find(({ key: tableKey }) => key === tableKey)?.name || key,
                            },
                        },

                        xAxis: {
                            label: {
                                formatter: (date) => dateFormat({ date, interval, language }),
                            },
                        },
                        yAxis:
                            typeParsed !== 'column'
                                ? {
                                      line: series?.some(({ type }) => type === 'bar') ? null : undefined,
                                      grid: {
                                          line: {
                                              style: {
                                                  stroke: '#c4c4c4',
                                                  opacity: 0.9,
                                                  lineDash: [2, 4],
                                              },
                                          },
                                      },
                                      label: {
                                          style: {
                                              fill: '#c4c4c4',
                                          },
                                          formatter: (value) => (log && (parseInt(value) || 0) === 0 ? `log 0` : value),
                                      },
                                      position: 'right',
                                      type: log ? 'log' : undefined,
                                      max: lineMaxY,
                                  }
                                : {
                                      type: log ? 'log' : undefined,
                                      position: 'left',
                                      max: barMaxY,
                                      label: {
                                          formatter: (value) => (log && (parseInt(value) || 0) === 0 ? `log 0` : value),
                                      },
                                  },

                        point: {
                            size: 5,
                            shape: 'diamond',
                            style: {
                                fill: 'white',
                                stroke: parseSeriesColor(table, index),
                                lineWidth: 1,
                            },
                        },
                    },
                };
            }),
            label: { autoRotate: true },

            tooltip: {
                shared: true,
                title: (date) => dateFormat({ date, interval, language }),
                fields: ['label', 'value', 'name'],
                formatter: (entry) => ({
                    name: entry.name,
                    value: entry.label,
                }),
            },

            syncViewPadding: true,

            className: 'chart',
        }),
        [dataTreated, series, stacked, log, interval, language],
    );

    if (query?.isLoading) {
        return (
            <div className="widget__empty">
                <WidgetLoading />
            </div>
        );
    }

    if (query?.status === 'error') {
        return <WidgetError />;
    }

    if (!(query?.data as any[])?.length) {
        return <></>;
    }
    return (
        <div className="widget__display">
            <Mix {...config} onReady={(chartRef) => setChart(chartRef)} />
            <ActionWidget
                onDownloadImage={() => chart.downloadImage(slugify(widget.options?.title || ''))}
                onDownloadCSV={() => {
                    const names = series.map(({ label }, index) => label || `agg_${index}`);

                    const tab = `Date,${names.join(',')}\n${query?.data
                        ?.map(
                            ({ _key, ...chart }: any) =>
                                `${f(_key)},${Object.values(chart)
                                    .map((value) => value || 0)
                                    .join(',')}`,
                        )
                        .join('\n')}`;

                    downloadFile(`${widget.options?.title || ''}.csv`, tab, 'text/csv');
                }}
            />
        </div>
    );
});
